/**
 * Frontend feature-module runtime (design v7, §5.1–5.3).
 *
 * Modules export `feature` from their routes file:
 *   { module: defineFrontendModule(identity, { codeDeps }),
 *     routes: [{ path, element, entitlement?, gate? }],
 *     settingsTabs: [{ tab, entitlement, element }] }
 *
 * The composer turns route descriptors into <Route> elements, wrapping each
 * in the entitlement gate for its attributed key (single-key modules attribute
 * implicitly; multi-key modules must attribute per descriptor — composition
 * throws otherwise). `gate: false` opts a descriptor out of entitlement
 * gating (platform-style pages), and extra non-entitlement guards stay inside
 * the descriptor's own element (design 5.3).
 */

import { Route } from 'react-router-dom'

export function defineFrontendModule(identity, policy) {
  return { identity, codeDeps: policy?.codeDeps ?? [] }
}

function resolveEntitlement(module, descriptor) {
  const keys = module.identity.entitlements
  if (descriptor.gate === false || keys === null) return null
  if (descriptor.entitlement) {
    if (!keys.includes(descriptor.entitlement)) {
      throw new Error(
        `feature "${module.identity.name}": route ${descriptor.path} attributes ` +
          `"${descriptor.entitlement}" which the module does not carry`,
      )
    }
    return descriptor.entitlement
  }
  if (keys.length === 1) return keys[0]
  throw new Error(
    `feature "${module.identity.name}" carries ${keys.length} keys — route ` +
      `${descriptor.path} must attribute one explicitly`,
  )
}

/**
 * Build the composed <Route> elements. `wrapGate(entitlementKey, element)`
 * is injected by App.jsx so the FeatureRoute component (and its error
 * states) stays owned there.
 */
export function composeFrontendRoutes(features, { wrapGate }) {
  const routes = []
  const seenPaths = new Set()
  for (const feature of features) {
    for (const descriptor of feature.routes) {
      if (seenPaths.has(descriptor.path)) {
        throw new Error(`duplicate composed route path: ${descriptor.path}`)
      }
      seenPaths.add(descriptor.path)
      const key = resolveEntitlement(feature.module, descriptor)
      const element = key ? wrapGate(key, descriptor.element) : descriptor.element
      routes.push(<Route key={descriptor.path} path={descriptor.path} element={element} />)
    }
  }
  return routes
}

/**
 * Integration pages by entitlement key ({ msgraph: '/microsoft-365' }): the
 * composed routes a module marks `integrationPage: true`, so Settings →
 * Available integrations can link an integration that is set up on its own
 * page rather than a settings tab (docs/architecture/integrations-layout.md).
 */
export function composeIntegrationPages(features) {
  const pages = {}
  for (const feature of features) {
    for (const descriptor of feature.routes) {
      if (!descriptor.integrationPage) continue
      const key = resolveEntitlement(feature.module, descriptor)
      if (key) pages[key] = descriptor.path
    }
  }
  return pages
}

/** Composed settings-tab contributions: [{ tab, entitlement, element }]. */
export function composeSettingsTabs(features) {
  const tabs = []
  for (const feature of features) {
    for (const tab of feature.settingsTabs ?? []) {
      const key = resolveEntitlement(feature.module, { path: `settings:${tab.tab}`, entitlement: tab.entitlement })
      tabs.push({ ...tab, entitlement: key, module: feature.module.identity.name })
    }
  }
  return tabs
}

/**
 * Frontend half of the architecture graph (design §5.6): a PURE descriptor-data
 * emitter — modules (name/entitlements/codeDeps), pages (path/entitlement), and
 * settings tabs (tab/entitlement), keyed by module name. It reads only the
 * composed descriptor data, NEVER the React element, so the vite build can run
 * it under jsdom/SSR. The worker joins this with its own half on feature
 * identity (worker/src/platform/architectureGraph.ts).
 */
export function composeFrontendGraph(features) {
  const modules = []
  const pages = []
  const tabs = []
  for (const feature of features) {
    const { identity, codeDeps } = feature.module
    modules.push({ name: identity.name, entitlements: identity.entitlements ?? null, codeDeps: codeDeps ?? [] })
    for (const descriptor of feature.routes ?? []) {
      pages.push({
        module: identity.name,
        path: descriptor.path,
        entitlement: resolveEntitlement(feature.module, descriptor),
      })
    }
    for (const tab of feature.settingsTabs ?? []) {
      tabs.push({
        module: identity.name,
        tab: tab.tab,
        entitlement: resolveEntitlement(feature.module, { path: `settings:${tab.tab}`, entitlement: tab.entitlement }),
      })
    }
  }
  return { modules, pages, tabs }
}
